import { FC, useState } from "react";
import { createPortal } from "react-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useData } from "data/data-provider";

export interface FleetCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  simpleImg?: boolean;
  onClick?: any;
  clickFunction?: any;
  loading?: boolean;
}

const FleetCard: FC<FleetCardProps> = ({
  size = "default",
  className = "",
  data,
  simpleImg = false,
  onClick,
  clickFunction,
  loading,
}) => {
  const { setSelectedCar } = useData();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const {
    id,
    fleet_photos,
    name,
    color,
    description,
    seats,
    avg_car_price_per_day,
    thumbnail_photo_url,
    doors,
    increased_price,
  } = data!;

  // "% OFF" badge + struck-through original price when the fleet-list
  // payload carries a higher increased_price than the current
  // avg_car_price_per_day (undocketed, ported from Template's FleetCard).
  const increasedPriceNum = Number(increased_price);
  const currentPriceNum = Number(avg_car_price_per_day ?? 0);
  const hasDiscount =
    Number.isFinite(increasedPriceNum) &&
    increasedPriceNum > 0 &&
    increasedPriceNum > currentPriceNum &&
    currentPriceNum > 0;
  const discountPercent = hasDiscount
    ? Math.round(
        ((increasedPriceNum - currentPriceNum) / increasedPriceNum) * 100
      )
    : 0;

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleMouseLeave = () => setIsHovered(false);

  const handleClick = async () => {
    setLoadingId(id);
    try {
      // No caller currently passes clickFunction on this card, which made
      // every click here throw (calling undefined) and get swallowed by the
      // try/finally with no visible error.
      await clickFunction?.(id);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden  transition-shadow ${className}`}
      data-nc-id="StayCard"
      onClick={onClick}
    >
      {/* Bookmark icon could be added here */}
      <div className="relative">
        <img
          src={
            // 1NOW-3011 / ONENOW-457: a car can have photos but no truthy
            // thumbnail_photo_url (or one already stored as an absolute URL) —
            // fall back to the placeholder instead of a broken/malformed src.
            fleet_photos &&
            fleet_photos.length > 0 &&
            thumbnail_photo_url?.includes("http")
              ? thumbnail_photo_url
              : fleet_photos && fleet_photos.length > 0 && thumbnail_photo_url
              ? `https://fleet-management-images-upload-be.s3.amazonaws.com/${encodeURIComponent(
                  thumbnail_photo_url
                )}`
              : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
          }
          alt={name}
          className="h-64 object-cover w-full"
        />
      </div>

      <div className="p-5 flex flex-col min-h-[220px]">
        <h2 className="text-lg font-semibold mb-1 truncate">{name}</h2>

        {/* <div className="min-h-[20px] mb-6">
          <p
            className="text-sm text-gray-500 truncate"
            onMouseEnter={description ? handleMouseEnter : undefined}
            onMouseLeave={description ? handleMouseLeave : undefined}
          >
            {description ? description.split(" ").slice(0, 4).join(" ") : ""}
          </p>
        </div> */}

        <div className="flex justify-between items-center border-t border-b py-4 mb-6">
          <div className="flex flex-col items-center">
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.9935 9.74463L15.0775 12.9321L14.015 13.9946L11.3256 11.8696H6.47791C5.99093 11.8696 5.53715 11.7036 5.11658 11.3716C4.71814 11.0396 4.47465 10.63 4.38611 10.1431L3.42322 5.95947C3.42322 5.93734 3.41215 5.89307 3.39001 5.82666C3.39001 5.76025 3.39001 5.71598 3.39001 5.69385C3.39001 5.31755 3.52283 4.97445 3.78845 4.66455C4.05408 4.35465 4.36397 4.1665 4.71814 4.1001H4.75134C4.77348 4.1001 4.80668 4.1001 4.85095 4.1001C4.91736 4.07796 4.96163 4.06689 4.98376 4.06689C5.36007 4.06689 5.6921 4.17757 5.97986 4.39893L7.14197 5.29541C8.20447 6.11442 9.31124 6.41325 10.4623 6.19189V7.71924C9.39978 7.89632 8.18233 7.60856 6.80994 6.85596L7.54041 9.74463H10.9935ZM10.8275 12.5669V13.9946H5.84705C5.0059 13.9946 4.24223 13.7069 3.55603 13.1313C2.86983 12.5558 2.46033 11.8475 2.32751 11.0063L0.932983 4.06689H2.32751L3.72205 10.7739C3.81059 11.283 4.05408 11.7147 4.45251 12.0688C4.85095 12.4009 5.3158 12.5669 5.84705 12.5669H10.8275ZM4.35291 3.33643C3.95447 3.40283 3.6003 3.32536 3.29041 3.104C2.98051 2.88265 2.79236 2.58382 2.72595 2.20752C2.65955 1.80908 2.73702 1.45492 2.95837 1.14502C3.17973 0.835124 3.47856 0.646973 3.85486 0.580566C4.2533 0.492025 4.60746 0.558431 4.91736 0.779785C5.22725 1.02327 5.41541 1.34424 5.48181 1.74268C5.57035 2.11898 5.50395 2.46208 5.28259 2.77197C5.06124 3.08187 4.75134 3.27002 4.35291 3.33643Z"
                fill="black"
              />
            </svg>

            <span className="text-sm">{seats} Seats</span>
          </div>

          <div className="flex flex-col items-center">
            <svg
              width="49"
              height="17"
              viewBox="0 0 49 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M23.9143 18.1965C21.6472 18.1965 19.38 18.1967 17.1127 18.1962C16.8742 18.1962 16.6381 18.1755 16.4176 18.0753C16.1561 17.9567 16.0114 17.7681 16.0121 17.4637C16.0166 15.0922 16.0205 12.7209 16.0103 10.3494C16.008 9.81734 16.1664 9.34735 16.4327 8.90238C16.6558 8.52962 16.9512 8.21529 17.253 7.90657C18.4321 6.70099 19.7911 5.74161 21.2804 4.95967C22.1924 4.48106 23.1395 4.08588 24.1161 3.76163C25.2087 3.39879 26.3255 3.13232 27.4614 2.94584C28.1632 2.83093 28.8688 2.74836 29.5774 2.69317C30.4982 2.62138 31.4214 2.60779 32.3445 2.62677C32.7643 2.63539 33.0532 2.99844 33.0051 3.41712C32.9486 3.90888 32.9074 4.40237 32.8589 4.89499C32.8121 5.36993 32.7634 5.84466 32.7173 6.31961C32.6701 6.80533 32.6254 7.29106 32.5784 7.77678C32.5334 8.24461 32.487 8.71244 32.4409 9.18027C32.4012 9.58364 32.3609 9.98701 32.3212 10.3906C32.273 10.8798 32.2249 11.3689 32.177 11.8581C32.1313 12.3259 32.0865 12.7938 32.0405 13.2614C31.9933 13.7434 31.9448 14.2253 31.8974 14.7074C31.8584 15.1036 31.8224 15.5003 31.7814 15.8965C31.7258 16.4353 31.6939 16.9773 31.6087 17.5122C31.5371 17.9613 31.2347 18.1969 30.7806 18.1967C28.4919 18.1962 26.203 18.1965 23.9143 18.1965ZM18.4385 10.2785C18.4646 10.3128 18.5021 10.313 18.5349 10.3199C18.8093 10.379 19.084 10.4124 19.3675 10.3915C19.9718 10.3468 20.472 10.0998 20.8734 9.6509C20.9303 9.58731 20.9883 9.56661 21.0694 9.56661C23.3505 9.5679 25.6319 9.56769 27.9131 9.56747C29.0087 9.56747 30.1045 9.56618 31.2002 9.56941C31.3058 9.56963 31.3414 9.54074 31.347 9.43143C31.3604 9.17725 31.3914 8.92415 31.4143 8.6704C31.4554 8.21658 31.4962 7.76298 31.5365 7.30917C31.5686 6.94827 31.6003 6.58715 31.6311 6.22626C31.6717 5.75455 31.7126 5.28283 31.7506 4.81091C31.778 4.47114 31.7976 4.13072 31.8273 3.79138C31.8426 3.61805 31.8383 3.60598 31.6641 3.6036C31.057 3.59498 30.4514 3.61632 29.8456 3.6616C29.2834 3.70364 28.7237 3.76465 28.1666 3.84765C27.3247 3.97291 26.4915 4.14279 25.6681 4.36054C24.4177 4.69126 23.2074 5.12826 22.0501 5.70733C21.1252 6.1702 20.2544 6.7191 19.4484 7.36824C19.2806 7.50341 19.1187 7.64592 18.9572 7.78842C18.86 7.87423 18.863 7.8766 18.9689 7.94128C19.0148 7.9693 19.0609 7.99733 19.1041 8.02967C19.5848 8.38949 19.8159 8.87931 19.8578 9.46981C19.8653 9.57674 19.814 9.6453 19.7422 9.71041C19.4895 9.94001 19.1946 10.0905 18.8673 10.1791C18.7272 10.2168 18.5838 10.2451 18.4385 10.2785ZM23.8975 17.0676C23.8975 17.0678 23.8975 17.0678 23.8975 17.0681C26.103 17.0681 28.3085 17.0681 30.514 17.0676C30.5603 17.0676 30.6082 17.0681 30.653 17.0577C30.7522 17.0351 30.8285 16.9478 30.8218 16.8742C30.8136 16.7852 30.7375 16.7307 30.6159 16.7283C30.5405 16.7268 30.465 16.7274 30.3896 16.7274C29.1467 16.7274 27.904 16.7279 26.6611 16.7279C23.5433 16.7279 20.4254 16.7276 17.3078 16.7276C17.2612 16.7276 17.214 16.7261 17.1678 16.7313C17.0779 16.7412 17.0228 16.7921 17.0143 16.8835C17.0059 16.9769 17.0577 17.0342 17.1424 17.0577C17.1862 17.07 17.2349 17.0674 17.2812 17.0674C19.4865 17.0678 21.692 17.0676 23.8975 17.0676ZM29.373 10.9748C29.373 10.9746 29.373 10.9746 29.373 10.9744C29.043 10.9744 28.7127 10.9733 28.3826 10.9746C28.1345 10.9757 27.9545 11.1415 27.9484 11.3715C27.9426 11.591 28.1194 11.7786 28.3624 11.7809C29.0298 11.7874 29.6973 11.7872 30.3645 11.7807C30.6157 11.7783 30.7865 11.5951 30.78 11.3651C30.7737 11.1376 30.5922 10.9753 30.3417 10.9748C30.019 10.9744 29.696 10.9748 29.373 10.9748Z"
                fill="black"
              />
            </svg>

            <span className="text-sm">{doors} Doors</span>
          </div>

          <div className="flex flex-col items-center">
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.1771 8.61961C15.1771 4.93771 12.1923 1.95294 8.51048 1.95294C4.82858 1.95294 1.84381 4.93771 1.84381 8.61961C1.84381 12.3015 4.82858 15.2863 8.51048 15.2863C9.07161 15.2863 9.84381 15.3638 9.84381 14.6196C9.84381 14.2136 9.63261 13.9004 9.42288 13.5892C9.11594 13.1339 8.81201 12.6831 9.17714 11.9529C9.62161 11.0641 10.3623 11.0641 11.4981 11.0641C12.0661 11.0641 12.7327 11.0641 13.5105 10.9529C14.9111 10.7529 15.1771 9.89188 15.1771 8.61961Z"
                fill="black"
              />
              <path
                d="M5.17719 10.621L5.18297 10.6194"
                stroke="white"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6.84381 7.28632C7.3961 7.28632 7.84381 6.8386 7.84381 6.28632C7.84381 5.73403 7.3961 5.28632 6.84381 5.28632C6.29153 5.28632 5.84381 5.73403 5.84381 6.28632C5.84381 6.8386 6.29153 7.28632 6.84381 7.28632Z"
                fill="black"
                stroke="white"
              />
              <path
                d="M11.5105 7.95294C12.0628 7.95294 12.5105 7.50523 12.5105 6.95294C12.5105 6.40066 12.0628 5.95294 11.5105 5.95294C10.9582 5.95294 10.5105 6.40066 10.5105 6.95294C10.5105 7.50523 10.9582 7.95294 11.5105 7.95294Z"
                fill="black"
                stroke="white"
              />
            </svg>

            <span className="text-sm">{color} Color</span>
          </div>
        </div>

        {/* Button at the bottom */}
        <div className="flex justify-between">
            <div className="flex flex-col">
            {hasDiscount && (
              <span className="text-gray-500 line-through text-sm">${increasedPriceNum}</span>
            )}
            <span className="font-semibold">
              ${avg_car_price_per_day}
              {hasDiscount && (
                <span className="ml-1 text-xs font-bold tracking-wide text-red-600">
                  {discountPercent}% OFF
                </span>
              )}
            </span>
            </div>
          <div>
            <ButtonPrimary
              className="w-full !bg-white !font-semibold !text-black border border-primary-100 rounded-md !text-xs !px-2 h-12"
              onClick={handleClick}
              disabled={loadingId === id}
            >
              {loadingId === id ? (
                <svg
                  aria-hidden="true"
                  className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              ) : (
                `  Rent Now`
              )}
            </ButtonPrimary>
          </div>
        </div>
      </div>

      {/* Description tooltip */}
      {isHovered &&
        description &&
        createPortal(
          <div
            className="absolute bg-white text-black text-xs rounded-md px-2 py-1 shadow-lg z-50"
            style={{
              top: tooltipPosition.top + 24,
              left: tooltipPosition.left,
              maxWidth: "250px",
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {description}
          </div>,
          document.body
        )}
    </div>
  );
};

export default FleetCard;
